import { alpha, type Theme } from '@mui/material/styles';
import type { SxProps } from '@mui/material';

/** Subtle row separators — softer than the default MUI `divider` border. */
export function gentleTableRowBorder(theme: Theme): string {
  return `1px solid ${alpha(theme.palette.divider, 0.15)}`;
}

/** Slightly stronger line under the header row. */
export function gentleTableHeaderBorder(theme: Theme): string {
  return `1px solid ${alpha(theme.palette.divider, 0.3)}`;
}

/** Sticky header cells inherit the parent surface (no filled table chrome). */
export function stickyHeaderCellBackground(): string {
  return 'transparent';
}

/** Keeps scrolled body rows from showing through the sticky header. */
export function stickyHeaderTableSx(stickyHeader: boolean): SxProps<Theme> {
  if (!stickyHeader) {
    return {};
  }

  return {
    '& .MuiTableHead-root .MuiTableCell-root': {
      position: 'sticky',
      top: 0,
      zIndex: 3,
      backgroundColor: stickyHeaderCellBackground(),
      borderBottom: (theme) => gentleTableHeaderBorder(theme),
      boxShadow: (theme) => `0 1px 0 ${alpha(theme.palette.divider, 0.2)}`,
    },
  };
}

/** Forces table chrome to inherit the parent surface (page, dialog, panel). */
export const enhancedTableSurfaceSx: SxProps<Theme> = {
  bgcolor: 'transparent',
  backgroundImage: 'none',
  '& .MuiTableHead-root': { bgcolor: 'transparent' },
  '& .MuiTableBody-root': { bgcolor: 'transparent' },
  '& .MuiTableRow-root': { bgcolor: 'transparent' },
  '& .MuiTableRow-root:hover': { bgcolor: 'action.hover' },
  '& .MuiTableCell-root': {
    bgcolor: 'transparent',
    borderBottom: (theme) => gentleTableRowBorder(theme),
  },
  '& .MuiTableHead-root .MuiTableCell-root': {
    borderBottom: (theme) => gentleTableHeaderBorder(theme),
  },
  '& .MuiTableRow-root.Mui-selected': { bgcolor: 'action.selected' },
  '& .MuiTableRow-root.Mui-selected:hover': { bgcolor: 'action.selected' },
  '& .EnhancedTable-groupHeader': {
    bgcolor: 'action.hover',
    '&:hover': { bgcolor: 'action.hover' },
  },
  '& .EnhancedTable-groupHeader .MuiTableCell-root': {
    bgcolor: 'action.hover',
    borderTop: 'none',
  },
};

/** @deprecated Use {@link enhancedTableSurfaceSx} — kept for imports that only need dividers. */
export const enhancedTableDividerSx: SxProps<Theme> = {
  '& .MuiTableCell-root': {
    borderBottom: (theme) => gentleTableRowBorder(theme),
  },
  '& .MuiTableHead-root .MuiTableCell-root': {
    borderBottom: (theme) => gentleTableHeaderBorder(theme),
  },
};
